import { type ChangeEvent, type RefObject, useRef, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { UserName } from "@/components/badges/UserName";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  ExternalLink,
  Globe,
  ImagePlus,
  MessageSquare,
  Pencil,
  Trash2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { useHasRole } from "@/hooks/useUserRoles";
import { getStorageUrl } from "@/lib/storage";
import {
  useCreateGeneralReply,
  useDeleteGeneralDiscussion,
  useDeleteGeneralReply,
  useGeneralDiscussion,
  useGeneralDiscussionReplies,
  useUpdateGeneralDiscussion,
  useUpdateGeneralReply,
} from "@/hooks/useGeneralDiscussions";

interface GeneralDiscussionThreadProps {
  discussionId: string;
  onBack: () => void;
}

interface ImageAttachmentFieldProps {
  imageUrl: string | null;
  uploading: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  disabled?: boolean;
}

function ImageAttachmentField({
  imageUrl,
  uploading,
  inputRef,
  onFileChange,
  onRemove,
  disabled,
}: ImageAttachmentFieldProps) {
  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFileChange}
        disabled={disabled || uploading}
      />
      {imageUrl ? (
        <div className="relative inline-block">
          <img
            src={imageUrl}
            alt="Reply attachment"
            className="mt-2 rounded-lg max-h-64 w-auto object-contain"
          />
          <button
            type="button"
            onClick={onRemove}
            className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full w-4 h-4 flex items-center justify-center text-xs"
            disabled={disabled || uploading}
          >
            ×
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
          className="inline-flex items-center gap-2"
        >
          <ImagePlus className="h-4 w-4" />
          {uploading ? "Uploading..." : "Add Photo"}
        </Button>
      )}
    </div>
  );
}

function LinkPreview({ linkUrl }: { linkUrl: string }) {
  return (
    <a
      href={linkUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-3 flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/50 hover:bg-muted transition-colors no-underline"
    >
      <Globe className="h-5 w-5 text-muted-foreground flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate">
          {linkUrl.replace(/^https?:\/\//, "")}
        </p>
        <p className="text-xs text-muted-foreground">Visit website</p>
      </div>
      <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    </a>
  );
}

const isValidLinkUrl = (url: string) => {
  if (!url.trim()) return true;
  return url.startsWith("http://") || url.startsWith("https://");
};

export function GeneralDiscussionThread({ discussionId, onBack }: GeneralDiscussionThreadProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = useHasRole("admin");
  const { data: discussion, isLoading: loadingDiscussion } = useGeneralDiscussion(discussionId);
  const { data: replies, isLoading: loadingReplies } = useGeneralDiscussionReplies(discussionId);
  const createReply = useCreateGeneralReply();
  const deleteReply = useDeleteGeneralReply();
  const deleteDiscussion = useDeleteGeneralDiscussion();
  const updateDiscussion = useUpdateGeneralDiscussion();
  const updateReply = useUpdateGeneralReply();

  const [replyContent, setReplyContent] = useState("");
  const [replyImageUrl, setReplyImageUrl] = useState<string | null>(null);
  const [replyLinkUrl, setReplyLinkUrl] = useState("");
  const [isUploadingReplyImage, setIsUploadingReplyImage] = useState(false);
  const [replyLinkError, setReplyLinkError] = useState(false);

  const [isEditingPost, setIsEditingPost] = useState(false);
  const [showDeletePostDialog, setShowDeletePostDialog] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editImageUrl, setEditImageUrl] = useState<string | null>(null);
  const [editLinkUrl, setEditLinkUrl] = useState("");
  const [isUploadingEditPostImage, setIsUploadingEditPostImage] = useState(false);
  const [editLinkError, setEditLinkError] = useState(false);

  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editReplyContent, setEditReplyContent] = useState("");
  const [editReplyImageUrl, setEditReplyImageUrl] = useState<string | null>(null);
  const [editReplyLinkUrl, setEditReplyLinkUrl] = useState("");
  const [isUploadingEditReplyImage, setIsUploadingEditReplyImage] = useState(false);
  const [editReplyLinkError, setEditReplyLinkError] = useState(false);

  const replyImageInputRef = useRef<HTMLInputElement>(null);
  const editPostImageInputRef = useRef<HTMLInputElement>(null);
  const editReplyImageInputRef = useRef<HTMLInputElement>(null);

  const canEditDiscussion = isAdmin || user?.id === discussion?.user_id;

  const uploadDiscussionImage = async (file: File) => {
    const fileExt = file.name.split(".").pop();
    if (!user?.id) throw new Error("Not authenticated");
    const fileName = `${user.id}/reply-${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from("discussion-images").upload(fileName, file);

    if (error) throw error;

    const { data } = supabase.storage.from("discussion-images").getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleReplyImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingReplyImage(true);
    try {
      const publicUrl = await uploadDiscussionImage(file);
      setReplyImageUrl(publicUrl);
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" });
    } finally {
      setIsUploadingReplyImage(false);
      e.target.value = "";
    }
  };

  const handleEditPostImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingEditPostImage(true);
    try {
      const publicUrl = await uploadDiscussionImage(file);
      setEditImageUrl(publicUrl);
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" });
    } finally {
      setIsUploadingEditPostImage(false);
      e.target.value = "";
    }
  };

  const handleEditReplyImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingEditReplyImage(true);
    try {
      const publicUrl = await uploadDiscussionImage(file);
      setEditReplyImageUrl(publicUrl);
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" });
    } finally {
      setIsUploadingEditReplyImage(false);
      e.target.value = "";
    }
  };

  const handleSubmitReply = async () => {
    const trimmedContent = replyContent.trim();
    const trimmedLink = replyLinkUrl.trim();

    if (!trimmedContent && !replyImageUrl && !trimmedLink) return;

    if (!isValidLinkUrl(trimmedLink)) {
      setReplyLinkError(true);
      return;
    }

    if (!user?.id) return;

    try {
      await createReply.mutateAsync({
        discussionId,
        content: trimmedContent,
        userId: user.id,
        image_url: replyImageUrl || undefined,
        link_url: trimmedLink || undefined,
        discussionTitle: discussion?.title,
      });
      setReplyContent("");
      setReplyImageUrl(null);
      setReplyLinkUrl("");
      setReplyLinkError(false);
      toast({ title: "Success", description: "Reply posted successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to post reply", variant: "destructive" });
    }
  };

  const handleStartEditPost = () => {
    if (!discussion) return;
    setEditTitle(discussion.title);
    setEditContent(discussion.content);
    setEditImageUrl(discussion.image_url);
    setEditLinkUrl(discussion.link_url || "");
    setEditLinkError(false);
    setIsEditingPost(true);
  };

  const handleSaveEditPost = async () => {
    const trimmedTitle = editTitle.trim();
    const trimmedContent = editContent.trim();
    const trimmedLink = editLinkUrl.trim();

    if (!trimmedTitle || (!trimmedContent && !editImageUrl && !trimmedLink)) return;

    if (!isValidLinkUrl(trimmedLink)) {
      setEditLinkError(true);
      return;
    }

    try {
      await updateDiscussion.mutateAsync({
        id: discussionId,
        title: trimmedTitle,
        content: trimmedContent,
        image_url: editImageUrl,
        link_url: trimmedLink || null,
      });
      setIsEditingPost(false);
      toast({ title: "Discussion updated" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update discussion.", variant: "destructive" });
    }
  };

  const handleDeleteDiscussion = async () => {
    try {
      await deleteDiscussion.mutateAsync(discussionId);
      setShowDeletePostDialog(false);
      toast({ title: "Discussion deleted" });
      onBack();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete discussion", variant: "destructive" });
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    try {
      await deleteReply.mutateAsync(replyId);
      toast({ title: "Success", description: "Reply deleted successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete reply", variant: "destructive" });
    }
  };

  const handleStartEditReply = (reply: {
    id: string;
    content: string;
    image_url: string | null;
    link_url: string | null;
  }) => {
    setEditingReplyId(reply.id);
    setEditReplyContent(reply.content);
    setEditReplyImageUrl(reply.image_url);
    setEditReplyLinkUrl(reply.link_url || "");
    setEditReplyLinkError(false);
  };

  const handleSaveEditReply = async () => {
    const trimmedContent = editReplyContent.trim();
    const trimmedLink = editReplyLinkUrl.trim();

    if (!editingReplyId || (!trimmedContent && !editReplyImageUrl && !trimmedLink)) return;

    if (!isValidLinkUrl(trimmedLink)) {
      setEditReplyLinkError(true);
      return;
    }

    try {
      await updateReply.mutateAsync({
        id: editingReplyId,
        content: trimmedContent,
        image_url: editReplyImageUrl,
        link_url: trimmedLink || null,
        discussionId,
      });
      setEditingReplyId(null);
      setEditReplyContent("");
      setEditReplyImageUrl(null);
      setEditReplyLinkUrl("");
      setEditReplyLinkError(false);
      toast({ title: "Reply updated" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update reply.", variant: "destructive" });
    }
  };

  if (loadingDiscussion) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!discussion) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-medium mb-2">Discussion not found</h3>
        <Button onClick={onBack} variant="outline">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={onBack} className="gap-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Discussions
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <UserAvatar avatarPath={discussion.author?.avatar_url} fullName={discussion.author?.full_name} className="h-10 w-10" fallbackClassName="bg-primary text-primary-foreground" thumbnailSize={64} />
            <div className="flex-1">
              {isEditingPost ? (
                <div className="space-y-3">
                  <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Discussion title" />
                  <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={5} placeholder="Write your discussion..." />
                  <ImageAttachmentField
                    imageUrl={editImageUrl}
                    uploading={isUploadingEditPostImage}
                    inputRef={editPostImageInputRef}
                    onFileChange={handleEditPostImageUpload}
                    onRemove={() => setEditImageUrl(null)}
                    disabled={updateDiscussion.isPending}
                  />
                  <div>
                    <Input
                      value={editLinkUrl}
                      onChange={(e) => {
                        setEditLinkUrl(e.target.value);
                        setEditLinkError(false);
                      }}
                      placeholder="Company / Website URL (optional) — https://..."
                      disabled={updateDiscussion.isPending}
                    />
                    {editLinkError && (
                      <p className="text-xs text-destructive mt-1">URL must start with http:// or https://</p>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsEditingPost(false)} disabled={updateDiscussion.isPending}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEditPost} disabled={updateDiscussion.isPending}>
                      {updateDiscussion.isPending ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-semibold">{discussion.title}</h2>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <span><UserName userId={discussion.user_id} name={discussion.author?.full_name} /></span>
                        <span>•</span>
                        <span>{formatDistanceToNow(new Date(discussion.created_at), { addSuffix: true })}</span>
                      </div>
                    </div>
                    {canEditDiscussion && (
                      <div className="flex gap-1 ml-auto">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleStartEditPost}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setShowDeletePostDialog(true)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {discussion.content && <p className="mt-4 whitespace-pre-wrap">{discussion.content}</p>}
                  {discussion.image_url && (
                    <img
                      src={discussion.image_url}
                      alt="Discussion attachment"
                      className="mt-2 rounded-lg max-h-64 w-auto object-contain"
                    />
                  )}
                  {discussion.link_url && <LinkPreview linkUrl={discussion.link_url} />}
                </>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="space-y-4">
        <h3 className="font-semibold">Replies ({replies?.length || 0})</h3>

        {loadingReplies ? (
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : replies && replies.length > 0 ? (
          <div className="space-y-3">
            {replies.map((reply) => {
              const canEdit = isAdmin || user?.id === reply.user_id;
              const isEditing = editingReplyId === reply.id;

              return (
                <Card key={reply.id}>
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <UserAvatar avatarPath={reply.author?.avatar_url} fullName={reply.author?.full_name} className="h-8 w-8" fallbackClassName="bg-muted" thumbnailSize={64} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 text-sm">
                            <UserName userId={reply.user_id} name={reply.author?.full_name} className="font-medium" />
                            <span className="text-muted-foreground">
                              {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          {canEdit && !isEditing && (
                            <div className="flex gap-1 ml-auto">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleStartEditReply(reply)}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete reply?</AlertDialogTitle>
                                    <AlertDialogDescription>Are you sure you want to delete this reply? This cannot be undone.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteReply(reply.id)}>Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="space-y-3 mt-3">
                            <Textarea
                              value={editReplyContent}
                              onChange={(e) => setEditReplyContent(e.target.value)}
                              rows={3}
                              placeholder="Write a reply..."
                            />
                            <ImageAttachmentField
                              imageUrl={editReplyImageUrl}
                              uploading={isUploadingEditReplyImage}
                              inputRef={editReplyImageInputRef}
                              onFileChange={handleEditReplyImageUpload}
                              onRemove={() => setEditReplyImageUrl(null)}
                              disabled={updateReply.isPending}
                            />
                            <div>
                              <Input
                                value={editReplyLinkUrl}
                                onChange={(e) => {
                                  setEditReplyLinkUrl(e.target.value);
                                  setEditReplyLinkError(false);
                                }}
                                placeholder="Company / Website URL (optional) — https://..."
                                disabled={updateReply.isPending}
                              />
                              {editReplyLinkError && (
                                <p className="text-xs text-destructive mt-1">URL must start with http:// or https://</p>
                              )}
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" onClick={() => setEditingReplyId(null)} disabled={updateReply.isPending}>
                                Cancel
                              </Button>
                              <Button onClick={handleSaveEditReply} disabled={updateReply.isPending}>
                                {updateReply.isPending ? "Saving..." : "Save"}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {reply.content && <p className="mt-2 whitespace-pre-wrap">{reply.content}</p>}
                            {reply.image_url && (
                              <img
                                src={reply.image_url}
                                alt="Reply attachment"
                                className="mt-2 rounded-lg max-h-64 w-auto object-contain"
                              />
                            )}
                            {reply.link_url && <LinkPreview linkUrl={reply.link_url} />}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No replies yet</p>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="space-y-3">
            <Textarea
              placeholder="Write a reply..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              rows={3}
            />
            <ImageAttachmentField
              imageUrl={replyImageUrl}
              uploading={isUploadingReplyImage}
              inputRef={replyImageInputRef}
              onFileChange={handleReplyImageUpload}
              onRemove={() => setReplyImageUrl(null)}
              disabled={createReply.isPending}
            />
            <div>
              <Input
                value={replyLinkUrl}
                onChange={(e) => {
                  setReplyLinkUrl(e.target.value);
                  setReplyLinkError(false);
                }}
                placeholder="Company / Website URL (optional) — https://..."
                disabled={createReply.isPending}
              />
              {replyLinkError && (
                <p className="text-xs text-destructive mt-1">URL must start with http:// or https://</p>
              )}
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleSubmitReply}
                disabled={createReply.isPending || (!replyContent.trim() && !replyImageUrl && !replyLinkUrl.trim())}
              >
                {createReply.isPending ? "Posting..." : "Post Reply"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeletePostDialog} onOpenChange={setShowDeletePostDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete discussion?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this discussion? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDiscussion}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
